import { Spacing } from '@persona-id/design-tokens';
import ZapIcon from '@persona-id/design-tokens/icons/ZapIcon';
import { Link, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ExternalIntegrationVendor } from 'dashboard/models/external-integration-vendor';
import ExternalIntegrationVendorModal from 'dashboard/views/DashboardExternalIntegrationsView/ExternalIntegrationVendorModal';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import {
  LEGACY_EXTERNAL_INTEGRATION_VENDOR_ICON,
  LEGACY_EXTERNAL_INTEGRATION_VENDORS,
  LegacyExternalIntegrationVendor,
  LegacyVendorName,
  LegacyVendorType,
} from 'lib/constants/externalIntegrationVendors';
import useToggle from 'lib/hooks/useToggle';

const VendorCardBanner = styled('div')`
  width: 100%;
  height: 120px;
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${({ theme }) => theme.card.borderRadius} ${({ theme }) => theme.card.borderRadius}
    0px 0px;
`;

const VendorCardBody = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${Spacing[4]};
`;

const VendorCardsContainer = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${Spacing[4]};
  > * {
    flex: 1;
    margin-bottom: 20px;
    min-width: 299px;
    max-width: 300px;
  }
`;

const VendorCardDescription = styled('div')`
  flex: 1;
`;

const VendorCardWrapper = styled('button')`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.color.border.primary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  cursor: pointer;
  height: 100%;
  transition: ${TransitionDuration.Fast};
  text-align: left;
  background-color: ${({ theme }) => theme.color.background.primary};
  :hover {
    transform: scale(1.02);
    border: 1px solid ${({ theme }) => theme.color.border.active};
  }
`;

const CardContent = styled('div')`
  display: flex;
  flex-direction: row;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  min-width: 20%;
  margin-top: ${Spacing[4]};
`;

const ImageContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  height: 80px;
  margin: 20px;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  border: 1.5px solid ${({ theme }) => theme.color.background.secondary};
`;

const Row = styled('div')`
  display: flex;
  flex-direction: column;
`;

interface ExternalIntegrationVendorsViewProps {
  externalIntegrationVendors: ExternalIntegrationVendor[];
}

const ExternalIntegrationVendorsView = ({
  externalIntegrationVendors,
}: ExternalIntegrationVendorsViewProps) => {
  const vendorFeatureFlags: Partial<Record<LegacyVendorName, boolean>> = {
    [LegacyVendorName.Kyckr]: false,
    [LegacyVendorName.Middesk]: false,
  };

  const enabledVendors = LEGACY_EXTERNAL_INTEGRATION_VENDORS.filter((vendor) => {
    return vendorFeatureFlags[vendor.name] ?? true;
  });

  const authenticationVendors = enabledVendors.filter((vendor) => {
    return vendor['vendor-type'] === LegacyVendorType.AUTHENTICATION;
  });

  const enrichmentVendors = enabledVendors.filter((vendor) => {
    return vendor['vendor-type'] === LegacyVendorType.ENRICHMENT;
  });

  const productivityVendors = enabledVendors.filter((vendor) => {
    return vendor['vendor-type'] === LegacyVendorType.PRODUCTIVITY;
  });

  return (
    <>
      <Card>
        <Card.Body>
          <CardContent>
            <ImageContainer>
              <ZapIcon />
            </ImageContainer>
            <Row>
              <Card.Title>Connect identity to your business operations</Card.Title>
              <Paragraph>
                Persona supports integrations with the top identity, risk, and business data
                vendors. We also integrate with the most used business systems, such as Slack,
                Salesforce, and more. Don&apos;t see what you need?{' '}
                <Link to='#' data-marketing-id='app-marketplace__integration-contact-us'>
                  Contact us
                </Link>
              </Paragraph>
            </Row>
          </CardContent>
          <Spacer height={1} />
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Card.Title>Productivity Apps</Card.Title>
          <Spacer height={1} />
          <VendorCardsContainer>
            {productivityVendors.map((vendor) => {
              return (
                <div key={vendor.name}>
                  <LegacyVendorCard vendor={vendor} />
                </div>
              );
            })}
          </VendorCardsContainer>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Card.Title>Data Apps</Card.Title>
          <Spacer height={1} />
          <VendorCardsContainer>
            {enrichmentVendors.map((vendor) => (
              <div key={vendor.name}>
                <LegacyVendorCard vendor={vendor} />
              </div>
            ))}
            {externalIntegrationVendors
              .filter((vendor) => vendor.vendor !== 'okta')
              .map((vendor) => (
                // todo: PER-48167 remove the harcode above after adding vendor type in open api spec
                <div key={vendor.vendor}>
                  <VendorCard vendor={vendor} />
                </div>
              ))}
          </VendorCardsContainer>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Card.Title>Authentication Apps</Card.Title>
          <Spacer height={1} />
          <VendorCardsContainer>
            {authenticationVendors.map((vendor) => {
              return (
                <div key={vendor.name}>
                  <LegacyVendorCard vendor={vendor} />
                </div>
              );
            })}
            {externalIntegrationVendors
              .filter((vendor) => vendor.vendor === 'okta')
              .map((vendor) => (
                // todo: PER-48167 remove the harcode above after adding vendor type in open api spec
                <div key={vendor.vendor}>
                  <VendorCard vendor={vendor} />
                </div>
              ))}
          </VendorCardsContainer>
        </Card.Body>
      </Card>
    </>
  );
};

interface VendorCardProps {
  vendor: LegacyExternalIntegrationVendor;
}

const LegacyVendorCard = ({ vendor }: VendorCardProps) => {
  const [showVendorModal, { enable: openVendorModal, disable: closeVendorModal }] =
    useToggle(false);

  const navigate = useNavigate();

  const handleClick = () => {
    // There exists both a legacy and new implementation of SentiLink.
    // If someone is first learning about SentiLink today, we want to nudge them to the new one.
    if (vendor.name === 'sentilink') {
      navigate(`/dashboard/integrations/${vendor.name}`);
    } else {
      vendor.model != null ? navigate(`/dashboard/marketplace/${vendor.name}`) : openVendorModal();
    }
  };

  const Icon = LEGACY_EXTERNAL_INTEGRATION_VENDOR_ICON(vendor);

  return (
    <>
      <ModalWrapper isOpen={showVendorModal} handleCloseModal={closeVendorModal}>
        <ExternalIntegrationVendorModal vendor={vendor} handleClose={closeVendorModal} />
      </ModalWrapper>
      <VendorCardWrapper
        id={`vendor-card-${vendor.name}`}
        data-marketing-id={`app-marketplace__vendor-${vendor.name}`}
        onClick={handleClick}
      >
        <VendorCardBanner>
          <ImageContainer>
            <Icon />
          </ImageContainer>
        </VendorCardBanner>
        <VendorCardBody>
          <VendorCardDescription>
            <Paragraph>{vendor.description}</Paragraph>
          </VendorCardDescription>
          <Spacer height={2} />
        </VendorCardBody>
      </VendorCardWrapper>
    </>
  );
};

const VendorCard = ({ vendor }: { vendor: ExternalIntegrationVendor }) => {
  const navigate = useNavigate();

  return (
    <>
      <VendorCardWrapper
        id={`vendor-card-${vendor.vendor}`}
        data-marketing-id={`app-marketplace__vendor-${vendor.vendor}`}
        onClick={() => navigate(`/dashboard/integrations/${vendor.vendor}`)}
      >
        <VendorCardBanner>
          <ImageContainer>
            <img
              src={vendor['logo-path'] || vendor['icon-path']}
              style={{ maxWidth: '100%', height: '100%' }}
              alt={`${vendor.title} logo`}
            />
          </ImageContainer>
        </VendorCardBanner>
        <VendorCardBody>
          <VendorCardDescription>
            <Paragraph>{vendor.description}</Paragraph>
          </VendorCardDescription>
          <Spacer height={2} />
        </VendorCardBody>
      </VendorCardWrapper>
    </>
  );
};

export default ExternalIntegrationVendorsView;
